import { ReactNode } from 'react';
import cx from 'classnames';

export type AlertTone = 'info' | 'warning' | 'danger' | 'success';

export interface AlertProps {
  tone?: AlertTone;
  title?: ReactNode;
  /** Sits before the text; takes the tone's colour. */
  icon?: ReactNode;
  /** The tone stays in the surface and the frame; the title is ink and the text a smaller muted line. */
  quiet?: boolean;
  className?: string;
  children?: ReactNode;
}

const toneClasses: Record<AlertTone, string> = {
  info: 'border-info-line bg-info-surface text-info',
  warning: 'border-warning-line bg-warning-surface text-warning',
  danger: 'border-transparent bg-danger-surface text-danger',
  success: 'border-transparent bg-success-surface text-success',
};

/** A notice inside the page flow. What happened after an action is a toast, not this. */
export const Alert = ({ tone = 'warning', title, icon, quiet, className, children }: AlertProps) => (
  <div
    role={tone === 'danger' ? 'alert' : undefined}
    className={cx(
      'flex items-start gap-3 rounded-control border text-ui',
      quiet ? 'p-4' : 'px-4 py-3',
      toneClasses[tone],
      className,
    )}
  >
    {icon && <span className="mt-0.5 shrink-0">{icon}</span>}
    {quiet ? (
      <div className="min-w-0">
        {title && <p className="text-ui-strong text-ink">{title}</p>}
        {children && <p className="mt-0.5 text-ui-xs text-ink-muted">{children}</p>}
      </div>
    ) : (
      <div className="min-w-0 leading-snug">
        {title && <p className="text-ui-strong leading-snug">{title}</p>}
        {children}
      </div>
    )}
  </div>
);
